// Meeting state: loading the meeting, following the events channel (OPEN-31), and
// updating the meeting's listenMode or title. The live stream carries `segment` and
// `ended` events via GET /api/meetings/:id/events.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MeetingEvent,
  MeetingSegment,
  MeetingSegmentEvent,
  MeetingEndedEvent,
  MeetingTranscriptResponse,
  type Meeting,
  type MeetingStatus,
} from '@jabberr/core';
import { api, ApiError, errorMessage, notifyUnauthorized } from '../lib/api.js';
import { parseJsonData, readSseStream } from '../lib/sse.js';

export interface MeetingState {
  project: { id: string; name: string } | null;
  /** The server's answer: the person who started the meeting and project owners. */
  canEnd: boolean;
  meeting: Meeting | null;
  segments: MeetingSegment[];
  loading: boolean;
  loadError: string | null;
  /** True once the `ended` event arrives. */
  ended: boolean;
  /** Last segment index seen, for reconnect resumption. */
  lastIndex: number;
  updateListenMode: (mode: 'always' | 'mentions' | 'keen' | 'silent') => Promise<void>;
  updateTitle: (title: string) => Promise<void>;
  reload: () => Promise<void>;
  endMeeting: () => Promise<void>;
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException ? err.name === 'AbortError' : err instanceof Error && err.name === 'AbortError';
}

export function useMeeting(meetingId: string): MeetingState {
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [project, setProject] = useState<{ id: string; name: string } | null>(null);
  const [canEnd, setCanEnd] = useState(false);
  const [segments, setSegments] = useState<MeetingSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ended, setEnded] = useState(false);
  const [lastIndex, setLastIndex] = useState(0);
  const mountedRef = useRef(true);
  const ownerId = useRef(meetingId);
  ownerId.current = meetingId;
  const indexedSegments = useRef(new Map<number, MeetingSegment>());
  const nextIndex = useRef(0);
  const acceptSegments = useCallback((from: number, incoming: MeetingSegment[]) => {
    incoming.forEach((segment, offset) => indexedSegments.current.set(from + offset, segment));
    nextIndex.current = Math.max(nextIndex.current, from + incoming.length);
    setSegments([...indexedSegments.current.entries()].sort(([a], [b]) => a - b).map(([, segment]) => segment));
    setLastIndex(Math.max(0, nextIndex.current - 1));
  }, []);

  const reload = useCallback(async (fromIndex?: number) => {
    try {
      const res = await api.meetings.transcript(meetingId, fromIndex ?? 0);
      if (!mountedRef.current || ownerId.current !== meetingId) return;
      // Only update segments if the response has segments (the initial load of the meeting row returns from:0, total:0, segments:[])
      if (res.segments.length > 0) {
        acceptSegments(res.from, res.segments);
      }
      setLoadError(null);
    } catch (err) {
      if (mountedRef.current && ownerId.current === meetingId) setLoadError(errorMessage(err));
    } finally {
      if (mountedRef.current && ownerId.current === meetingId) setLoading(false);
    }
  }, [meetingId, acceptSegments]);

  const reloadMeeting = useCallback(async () => {
    try {
      const { meeting: m, project: p, canEnd: mayEnd } = await api.meetings.get(meetingId);
      if (!mountedRef.current || ownerId.current !== meetingId) return;
      setProject(p);
      setCanEnd(mayEnd);
      if (!mountedRef.current || ownerId.current !== meetingId) return;
      setMeeting(m);
      // Also load transcript segments
      const res = await api.meetings.transcript(meetingId, 0);
      if (!mountedRef.current || ownerId.current !== meetingId) return;
      acceptSegments(res.from, res.segments);
      setLoadError(null);
    } catch (err) {
      if (mountedRef.current && ownerId.current === meetingId) setLoadError(errorMessage(err));
    } finally {
      if (mountedRef.current && ownerId.current === meetingId) setLoading(false);
    }
  }, [meetingId, acceptSegments]);

  // SSE handler: process segment and ended events
  const applyEvent = useCallback(
    (msg: { event: string; data: unknown }) => {
      if (ownerId.current !== meetingId) return;
      const parsed = MeetingEvent.safeParse({ event: msg.event, data: msg.data });
      if (!parsed.success) return;
      const ev = parsed.data;
      if (ev.event === 'segment') {
        const seg = ev.data as MeetingSegmentEvent;
        // Provider deltas can share timestamps and text. Only the append index
        // identifies a duplicate; distinct fragments must all reach rendering.
        acceptSegments(seg.index, [seg]);
      } else if (ev.event === 'ended') {
        const ed = ev.data as MeetingEndedEvent;
        setEnded(true);
        // Update the meeting row's endedAt
        setMeeting((prev) => (prev ? { ...prev, status: 'ended', endedAt: ed.endedAt } : null));
      }
    },
    [meetingId, acceptSegments],
  );

  // The live events channel.
  useEffect(() => {
    mountedRef.current = true;
    let stopped = false;
    const controller = new AbortController();

    let reconnectHandle: ReturnType<typeof setTimeout> | null = null;
    const reconnect = async (): Promise<void> => {
      reconnectHandle = setTimeout(async () => {
        if (stopped) return;
        // Fetch transcript from after the last seen index to catch up, then re-open SSE.
        try {
          const res = await fetch(`/api/meetings/${meetingId}/transcript?after=${nextIndex.current}`, {
            headers: { accept: 'application/json' },
            credentials: 'same-origin',
          });
          if (!stopped && res.ok) {
            const data = MeetingTranscriptResponse.parse(await res.json());
            if (data.segments && data.segments.length > 0) {
              acceptSegments(data.from, data.segments);
            }
          }
        } catch {
          // ignore
        }
        await rejoinStream();
      }, 2000);
    };

    const rejoinStream = async (): Promise<void> => {
      const res = await fetch(`/api/meetings/${meetingId}/events`, {
        headers: { accept: 'text/event-stream' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (stopped) return;
      if (res.status === 401) {
        notifyUnauthorized();
        return;
      }
      if (!res.ok || !res.body) throw new Error(`events: HTTP ${res.status}`);
      for await (const msg of readSseStream(res.body, controller.signal)) {
        if (stopped) return;
        applyEvent({ event: msg.event, data: parseJsonData(msg.data) });
      }
      // Stream ended normally (not aborted): reconnect.
      if (stopped) return;
      await reconnect();
    };

    void rejoinStream().catch((err) => {
      if (isAbort(err) || stopped) return;
      reconnect();
    });

    return () => {
      stopped = true;
      mountedRef.current = false;
      controller.abort();
      if (reconnectHandle) clearTimeout(reconnectHandle);
    };
  }, [meetingId, applyEvent, acceptSegments]);

  // Initial load.
  useEffect(() => {
    setSegments([]);
    indexedSegments.current.clear();
    nextIndex.current = 0;
    setMeeting(null);
    setLoadError(null);
    setEnded(false);
    setLastIndex(0);
    setLoading(true);
    void reloadMeeting();
  }, [reloadMeeting]);

  const updateListenMode = useCallback(
    async (mode: 'always' | 'mentions' | 'keen' | 'silent') => {
      if (!meeting) return;
      try {
        const { meeting: m } = await api.meetings.update(meetingId, { listenMode: mode });
        if (!mountedRef.current) return;
        setMeeting((prev) => {
          if (prev === null) return prev;
          return { ...prev, listenMode: m.listenMode as 'always' | 'mentions' | 'keen' | 'silent', title: m.title };
        });
      } catch (err) {
        // best effort
      }
    },
    [meetingId, meeting],
  );

  const updateTitle = useCallback(
    async (title: string) => {
      if (!meeting) return;
      try {
        const { meeting: m } = await api.meetings.update(meetingId, { title });
        if (!mountedRef.current) return;
        setMeeting((prev) => {
          if (prev === null) return prev;
          return { ...prev, title: m.title };
        });
      } catch (err) {
        // best effort
      }
    },
    [meetingId, meeting],
  );

  const endMeeting = useCallback(async () => {
    // Let the caller report a failed hangup and offer retry.
    const { meeting: m } = await api.meetings.end(meetingId);
    if (!mountedRef.current) return;
    setMeeting((prev) => prev === null ? prev : { ...prev, ...m });
    setEnded(true);
  }, [meetingId]);

  return {
    meeting,
    project,
    canEnd,
    segments,
    loading,
    loadError,
    ended,
    lastIndex,
    updateListenMode,
    updateTitle,
    reload,
    endMeeting,
  };
}
